import {NotFoundError} from '@packages/utils';
import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import * as z from 'zod';
import type {PostSchema} from '../../core/entities/columns/schema';
import type {GetRecruitmentListResponse} from '../../core/entities/recruitment/schema';
import {getPost} from '../../core/repositories/columnRepository';
import {
  type GetCompanyInfoResponse,
  getCompanyInfo,
} from '../../core/repositories/companiesRepository';
import {getRecruitmentList} from '../../core/repositories/recruitmentRepository';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../../core/repositories/usersMeRepository';
import {validateCookie} from '../../core/usecases/auth/checkOauthTokenExistence';
import {ArchivePage} from '../../features/columns/components/ArchivePage';
import {handleParsedContent} from '../../features/columns/usecases/handleParsedContent';
import {handlePostRedirect} from '../../features/columns/usecases/handlePostRedirect';
import {
  PostPageType,
  handlePostRedirectToPreviewPage,
} from '../../features/columns/usecases/handlePostRedirectToPreviewPage';
import {withHandler} from '../../server/token/withHandler';
import {ScriptForGA} from '../../shared/components/ScriptForGA';
import {NoLeftSidebarLayout} from '../../shared/components/layouts/NoLeftSidebarLayout';
import {UnexpectedError} from '../../utils/unexpectedError';
import type {NextPageWithLayout} from '../_app';

type Props = {
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
  post: PostSchema;
  parsedContent: string;
  companyInfo?: GetCompanyInfoResponse;
  recruitmentList?: GetRecruitmentListResponse;
  metaDescription: string;
  eyeCatchImageURL?: string;
};

const QueryParameter = z.object({
  id: z.string(),
  preview: z.string().optional(),
  preview_code: z.string().optional(),
});

type QueryParameter = z.infer<typeof QueryParameter>;

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession' | 'withQuery',
  QueryParameter
>(
  {auth: 'optional', queryValidator: QueryParameter},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const {id, preview, preview_code} = context.validatedQuery;
    if (!id) {
      // 存在しない場合は、コラムトップにリダイレクト
      return {
        redirect: {
          destination: '/column/categories',
          permanent: true,
        },
      };
    }

    const {cookies} = context.req;
    const {isValid} = validateCookie(cookies);

    const redirectToPreviewPage = await handlePostRedirectToPreviewPage({
      cookies,
      wpPreviewParam: preview ?? '',
      postPageType: PostPageType.ARCHIVES,
      columnId: id,
    });
    if (redirectToPreviewPage !== undefined) {
      return redirectToPreviewPage;
    }

    const getPostResponse = await getPost({
      cookies,
      columnId: id,
      previewCode: preview_code ?? undefined,
    });
    if (!getPostResponse.ok) {
      if (getPostResponse.error === NotFoundError) {
        return {
          redirect: {
            destination: '/column/categories',
            permanent: true,
          },
        };
      }

      throw new Error(getPostResponse.error);
    }

    const redirect = handlePostRedirect({
      post: getPostResponse.value.post,
      isSpecialPost: false,
    });
    if (redirect !== undefined) {
      return redirect;
    }

    const {parsedContent, metaDescription, eyeCatchImageURL} =
      await handleParsedContent(
        getPostResponse.value.post.content,
        getPostResponse.value.post.contentHTML,
        getPostResponse.value.post.description,
      );

    let props: Props = {
      post: getPostResponse.value.post,
      parsedContent,
      metaDescription,
      eyeCatchImageURL,
    };

    const {companyPages, sponsorCompanies} = getPostResponse.value.post;
    const isCompanyStudyPost = companyPages && companyPages?.length > 0;
    const isSponsoredPost = sponsorCompanies && sponsorCompanies?.length > 0;
    if (isCompanyStudyPost || isSponsoredPost) {
      const companyId = isSponsoredPost
        ? sponsorCompanies?.[0]?.id
        : companyPages?.[0]?.companyID;

      if (companyId === undefined) {
        throw new UnexpectedError('companyId is undefined');
      }

      const getCompanyInfoResponse = await getCompanyInfo({
        companyId,
        cookies: context.req.cookies,
      });
      if (!getCompanyInfoResponse.ok) {
        throw new Error(getCompanyInfoResponse.error);
      }

      const getRectuitmentListResponse = await getRecruitmentList({
        cookies,
        companyIDs: [companyId],
      });
      if (!getRectuitmentListResponse.ok) {
        throw new Error(getRectuitmentListResponse.error);
      }

      props = {
        ...props,
        companyInfo: getCompanyInfoResponse.value,
        recruitmentList: getRectuitmentListResponse.value,
      };
    }

    if (!isValid) return {props};

    const userDataResponse = await getUserDataForAnalytics({cookies});
    if (!userDataResponse.ok) {
      return {
        props,
      };
    }

    return {
      props: {
        ...props,
        userDataForAnalytics: userDataResponse.value,
      },
    };
  },
);

const Page: NextPageWithLayout<Props> = ({
  userDataForAnalytics,
  post,
  parsedContent,
  companyInfo,
  recruitmentList,
  metaDescription,
  eyeCatchImageURL,
}: Props) => {
  return (
    <>
      <ArchivePage
        post={post}
        parsedContent={parsedContent}
        companyInfo={companyInfo}
        recruitmentList={recruitmentList}
        metaDescription={metaDescription}
        eyeCatchImageURL={eyeCatchImageURL}
      />
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

export default Page;

Page.getLayout = (page) => <NoLeftSidebarLayout>{page}</NoLeftSidebarLayout>;
